<template>
  <DefaultBackground>
    <section v-if="caterer" class="m-2 md:m-10">
      <div class="mx-auto max-w-8xl">
        <div class="grid grid-cols-12 gap-6 mx-auto">
          <!-- Left panel -->
          <div class="col-span-12 lg:col-span-3">
            <div class="bg-white rounded-lg shadow-card mx-auto px-6 py-6 mb-6">
              <h1 class="font-semibold text-2xl mb-3">Categories</h1>

              <ul class="list-disc ml-5">
                <li
                  class="py-2 cursor-pointer hover:font-semibold"
                  v-for="(category, index) in categories"
                  :key="index"
                  v-scroll-to="{el: '#category' + category.id, offset: -105}"
                >
                  {{ category.name }}
                </li>
              </ul>
            </div>
          </div>

          <!-- Middle -->
          <div class="col-span-12 lg:col-span-6">
            <div
              v-if="caterer.status == 'pending'"
              class="bg-yellow-500 rounded-lg shadow-card mx-auto mb-6 p-4  text-semibold"
            >
              This Account is Under Review and Will Be Active Upon Verification
            </div>

            <!-- MENU -->
            <div class="bg-white rounded-lg shadow-card mx-auto mb-6">
              <header
                class="flex flex-col md:flex-row justify-between items-center px-6 py-4"
              >
                <h2 class="font-semibold text-2xl lg:text-2xl py-2">
                  Menu
                </h2>

                <a
                  v-if="canMakeOrder"
                  @click="$router.push(`/caterers/${caterer.uuid}/order`)"
                  class="btn btn-secondary font-semibold rounded-lg px-4"
                  >Plan an Event with this Caterer</a
                >
              </header>

              <div
                class="notification py-4 px-8 border flex items-center"
                v-if="buildaboxmenu"
              >
                <div
                  class="bg-gray-200 w-6 h-6 text-center text-sm font-semibold text-red-500 block rounded-full flex items-center justify-center flex-shrink-0"
                >
                  <span>i</span>
                </div>
                <p class="ml-4">
                  This is a sample menu. The menu is finalized during the event
                  planning stage.
                </p>
              </div>

              <!-- Categories -->
              <div v-if="categories.length > 0">
                <div
                  v-for="(category, index) in categories"
                  :key="category.uuid"
                  :class="{
                    'border-b border-gray-300': index < categories.length - 1
                  }"
                  class="px-3 py-5 cursor-pointer"
                >
                  <div
                    class="flex flex-row items-center justify-between px-2 md:px-4 group"
                    :id="'category' + category.id"
                  >
                    <div class="img-description flex items-center">
                      <div
                        class="w-12 h-12 rounded bg-gray-600 overflow-hidden flex items-center justify-center flex-shrink-0 cursor-pointer"
                      >
                        <LightBox :img="category.image">
                          <img :src="category.image" :alt="category.name" />
                        </LightBox>
                      </div>
                      <div
                        class="ml-4 min-w-0"
                        @click="category.isExpanded = !category.isExpanded"
                      >
                        <div class="flex items-center">
                          <div class="font-semibold text-primary ">
                            {{ category.name }}
                          </div>
                        </div>
                        <div class="text-gray-600 mt-1">
                          {{ category.description }}
                        </div>
                      </div>
                    </div>
                    <ArrowIcon
                      class="flex-shrink-0 ml-1"
                      width="22"
                      height="14"
                      :direction="category.isExpanded ? 'top' : 'bottom'"
                      @click="category.isExpanded = !category.isExpanded"
                    />
                  </div>
                  <div
                    class="items mt-4 md:ml-10 max-h-0 overflow-hidden ease-in-out duration-700"
                    :class="[!category.isExpanded ? 'max-h-0' : 'max-h-screen']"
                  >
                    <div
                      v-if="category.items && category.items.length > 0"
                      class=""
                    >
                      <div
                        v-for="item in category.items"
                        :key="item.uuid"
                        class="line-item"
                      >
                        <div class="flex flex-row items-center py-4 group">
                          <div
                            class="btn ml-3 draggable-handler mr-4 flex-shrink-0"
                          ></div>
                          <div
                            class="w-12 h-12 rounded bg-gray-600 overflow-hidden flex items-center justify-center flex-shrink-0 cursor-pointer"
                          >
                            <LightBox :img="item.image">
                              <img
                                :src="item.image"
                                alt="BuildaBox Order Details Page"
                              />
                            </LightBox>
                          </div>
                          <div class="ml-4 min-w-0">
                            <div class="flex items-center">
                              <div class="font-semibold">
                                {{ item.name }}
                              </div>
                            </div>
                            <div class="text-gray-600 mt-1">
                              {{ item.description }}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div
                      v-else
                      class="text-center text-gray-600 my-2 p-5"
                    ></div>
                  </div>
                </div>
              </div>
              <div class="p-6" v-else>
                <div class="text-center text-white opacity-30 p-4 bg-green-500">
                  Menu is empty
                </div>
              </div>
            </div>

            <!-- Territories table -->
            <div class="bg-white rounded-lg shadow-card mx-auto">
              <header
                class="flex flex-col md:flex-row justify-between px-6 py-4"
              >
                <h2 class="font-semibold text-2xl lg:text-2xl py-2">
                  Territories
                </h2>
              </header>

              <div>
                <table class="table-fixed text-left w-full text-sm">
                  <thead class="hidden md:table-header-group">
                    <tr
                      v-if="
                        caterer.service_zip_codes &&
                          caterer.service_zip_codes.length > 0
                      "
                      class="border-t border-b border-light-gray px-6 py-4"
                    >
                      <th class="px-6 py-3">
                        <span class="text-dark-gray font-normal"
                          >POSTAL CODE</span
                        >
                      </th>
                    </tr>
                  </thead>
                  <tbody
                    v-if="
                      caterer.service_zip_codes &&
                        caterer.service_zip_codes.length > 0
                    "
                  >
                    <tr
                      v-for="location in caterer.service_zip_codes"
                      :key="location"
                      class="border-t border-light-gray"
                    >
                      <td class="block md:table-cell px-6 py-4 md:py-5">
                        <div class="md:hidden text-dark-gray mb-2">
                          POSTAL CODE
                        </div>
                        <div>{{ location }}</div>
                      </td>
                    </tr>
                  </tbody>

                  <tbody v-else-if="!loading">
                    <tr class="border-t border-light-gray">
                      <td class="px-6 py-4">
                        <div
                          class="text-center text-white opacity-30 p-4 bg-green-500"
                        >
                          No Territories available.
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <!-- Right Panel -->
          <div class="col-span-12 lg:col-span-3">
            <div class="bg-white rounded-lg shadow-card mx-auto px-6 py-6 mb-6">
              <h1 class="font-semibold text-2xl mb-3">
                {{ caterer.name }}
              </h1>
              <div class="mb-8 relative">
                <img
                  :src="caterer.logo"
                  v-if="caterer.logo"
                  :alt="caterer.name"
                />
                <CatererLogoPlaceholder v-else :name="caterer.name" />
              </div>
              <div class="caterer-info mb-8">
                <div class="flex justify-between content-center">
                  <div class="text-lg font-semibold mb-2">Description</div>
                </div>
                <div class="text-sm">
                  {{ caterer.description }}
                </div>
              </div>

              <div v-if="caterer.email" class="caterer-info mb-8">
                <div class="flex justify-between content-center">
                  <div class="text-lg font-semibold mb-2">Email</div>
                </div>
                <div class="text-sm">
                  {{ caterer.email }}
                </div>
              </div>

              <div class="caterer-info mb-8" v-if="caterer.website">
                <div class="flex justify-between content-center">
                  <div class="text-lg font-semibold mb-2">Website</div>
                </div>
                <div class="text-sm">
                  {{ caterer.website }}
                </div>
              </div>

              <div v-if="caterer.cuisine_types" class="caterer-info mb-8">
                <div class="flex justify-between content-center">
                  <div class="text-lg font-semibold mb-2">Cuisine Types</div>
                </div>
                <div class="tags flex flex-wrap -m-1">
                  <span
                    v-for="type in caterer.cuisine_types"
                    :key="type.id"
                    class="m-1"
                  >
                    {{ type.name }}
                  </span>
                </div>
              </div>

              <div
                v-if="caterer.contact_first_name || caterer.contact_last_name"
                class="caterer-info mb-8"
              >
                <div class="flex justify-between content-center">
                  <div class="text-lg font-semibold mb-2">Contact Person</div>
                </div>
                <div class="text-sm">
                  {{ caterer.contact_first_name }}
                  {{ caterer.contact_last_name }}
                </div>
              </div>
              <div class="caterer-info mb-8">
                <div class="flex justify-between content-center">
                  <div class="text-lg font-semibold mb-2">Address</div>
                </div>
                <div class="text-sm">
                  {{ caterer.address }}
                </div>
              </div>
              <div class="caterer-info mb-8">
                <div class="flex justify-between content-center">
                  <div class="text-lg font-semibold mb-2">State</div>
                </div>
                <div class="text-sm">
                  {{ caterer.state }}
                </div>
              </div>
              <div class="caterer-info mb-8">
                <div class="flex justify-between content-center">
                  <div class="text-lg font-semibold mb-2">Zip</div>
                </div>
                <div class="text-sm">
                  {{ caterer.zip }}
                </div>
              </div>
              <div class="caterer-info">
                <div class="flex justify-between content-center">
                  <div class="text-lg font-semibold mb-2">Phone</div>
                </div>
                <div class="text-sm">
                  {{ caterer.phone }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </DefaultBackground>
</template>

<script>
import DefaultBackground from "@/components/common/layout/DefaultBackground";
import Api from "@/api/api_v1.0";
import CatererLogoPlaceholder from "@/components/caterer/CatererLogoPlaceholder";
import LightBox from "@/components/common/LightBoxComponent";
import ArrowIcon from "@/components/common/icons/ArrowIcon";

export default {
  name: "CatererDashboard",

  components: {
    DefaultBackground,
    CatererLogoPlaceholder,
    LightBox,
    ArrowIcon
  },

  data() {
    return {
      loading: true,
      caterer: {
        payments: []
      },
      categories: [],
      buildaboxmenu: false
    };
  },

  async created() {
    await this.getCatererInfo(this.$route.params.slug);
    await this.getCategories();
  },

  methods: {
    async getCatererInfo(slug) {
      const response = await Api.getCatererBySlug(slug);
      this.caterer = response.data;
      this.loading = false;
    },

    async getCategories() {
      try {
        const response = await Api.getCatererMenu(this.caterer.id);
        let categories = response.data.menu;
        categories = categories.map(function(category) {
          return {
            ...category,
            isExpanded: false
          };
        });
        this.categories = categories;
        this.buildaboxmenu = response.data.buildaboxmenu;
      } catch (e) {
        console.error(e);
      }
    }
  },

  computed: {
    canMakeOrder() {
      return (
        (this.caterer.status == "active" ||
          this.caterer.status == "imported") &&
        this.categories.length > 0
      );
    }
  }
};
</script>
